import React, { useState, useEffect, useContext } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Alert,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  FormFeedback,
  Form,
} from 'reactstrap';

// Formik Validation
import * as Yup from 'yup';
import { useFormik } from 'formik';

//redux
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import withRouter from 'components/Common/withRouter';

//Import Breadcrumb
import Breadcrumb from '../../components/Common/Breadcrumb';

import avatar from '../../assets/images/users/avatar-1.jpg';
// actions
import { useMutation, useQueryClient } from 'react-query';
import { getAppUserByIdApi } from 'api/user';
import { sendEmailForMFA, resetPasswordApi } from 'api/auth';
import Spinners from 'components/Common/Spinner';

const UserProfile = () => {
  document.title = 'Profile | SUN Welfare Administration System';
  const queryClient = useQueryClient();
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const userId = localStorage.getItem('userId');

  const selectProfileState = (state) => state.Profile;
  const ProfileProperties = createSelector(selectProfileState, (profile) => ({
    error: profile.error,
    success: profile.success,
  }));

  const { error, success } = useSelector(ProfileProperties);

  const {
    mutate: mutateUserDetails,
    isLoading: isLoadingUserDetails,
    data: userDetailsData,
  } = useMutation((id) => getAppUserByIdApi(userId), {
    onSettled: () => {
      queryClient.invalidateQueries('get-all-counties');
    },
  });

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      confirmPassword: '',
      password: '',
      otp: '',
    },
    validationSchema: Yup.object({
      confirmPassword: Yup.string()
        .required('Please confirm your password')
        .oneOf([Yup.ref('password')], 'Passwords must match!!'),
      password: Yup.string()
        .required('Please enter a password')
        .min(6, 'Password must be at least 6 characters')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[@$!%*?&]/, 'Password must contain at least one special character'),
      otp: Yup.string().required('Please the OTP code'),
    }),

    onSubmit: async (values) => {
      try {

        const resetPasswordResponse = await resetPasswordApi({
          userName: userDetailsData.email,
          password: validation.values.password,
          confirmPassword: validation.values.confirmPassword,
          otpCode: validation.values.otp,
        });
        if (resetPasswordResponse.status === 'Success') {
          setSuccessMessage('Password reset successful');
          setShowResetPasswordModal(false);
          return;
        }
        setSuccessMessage('');
        setErrorMessage(resetPasswordResponse.message || 'An error occurred while resetting password');

      } catch (error) {
        console.error('Error resetting password:', error);
        setSuccessMessage('');
        setErrorMessage(error.message || 'An error occurred while resetting password');
        // setShowResetPasswordModal(false);
      }
    }

  });

  useEffect(() => {
    if (userId) {
      mutateUserDetails(userId);
    }
  }, [userId]);

  const handleResetPassword = async () => {
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await sendEmailForMFA(userDetailsData?.email);
      setShowResetPasswordModal(true);
      setSuccessMessage('An OTP has been sent to your email. Please check your inbox and enter the OTP to reset your password.');
    } catch (error) {
      console.error('Error sending OTP email:', error);
      setErrorMessage(error.response?.data?.message || error.message || 'An error occurred while sending OTP email. Please try again later.');
    }
  };

  if (isLoadingUserDetails) return <Spinners />;

  const primaryAddress = userDetailsData?.addresses?.find(
    (address) => address.primary === true
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {/* Render Breadcrumb */}
          <Breadcrumb title="User" breadcrumbItem="Profile" />
          <Card className="my-3">
            <CardBody>
              {error && error ? <Alert color="danger">{error}</Alert> : null}
              {success ? <Alert color="success">{success}</Alert> : null}
              <Row>
                <img
                  src={userDetailsData?.profileImageUrl || avatar}
                  alt=""
                  className="avatar-md rounded-circle img-thumbnail mb-5 me-3"
                />
                <Row>
                  <Col md="6">
                    <div>
                      <h5 className="mb-3">{`${userDetailsData?.firstName} ${userDetailsData?.middleName || ''} ${userDetailsData?.lastName || ''}`}</h5>
                      <p className="text-muted mb-3">EMAIL: <span> <strong>{userDetailsData?.email || ''}</strong></span></p>
                      <p className="text-muted mb-3">ROLE: <span> <strong>{userDetailsData?.role || ''}</strong></span></p>
                      <p className="text-muted mb-3">
                        MOBILE: <span> <strong>{userDetailsData?.mobileNumber}</strong></span>
                      </p>
                    </div>
                  </Col>
                  <Col md="6">
                    <div>
                      <h5 className="mb-3">Address</h5>
                      <p className="text-muted mb-3">
                        County: <span> <strong>{primaryAddress?.countyName || 'N/A'}</strong></span>
                      </p>
                      <p className="text-muted mb-3">
                        Constituency: <span> <strong>{primaryAddress?.constituencyName || 'N/A'}</strong></span>
                      </p>
                      WARDS:
                      {
                        userDetailsData?.addresses?.map(a => (
                          <span key={a.id}> <strong><p className="text-muted mb-3">
                            Ward:{' '}
                            <span>
                              <strong>{primaryAddress?.wardName || 'N/A'}</strong>
                            </span>
                          </p>
                          </strong>
                          </span>
                        ))
                      }
                    </div>
                  </Col>
                </Row>
              </Row>
              <Row>
                <Col md={6}>
                  <Button color="primary" onClick={handleResetPassword}>
                    Change Password
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal
        isOpen={showResetPasswordModal}
        toggle={() => setShowResetPasswordModal(!showResetPasswordModal)}
      >
        <ModalHeader
          toggle={() => setShowResetPasswordModal(!showResetPasswordModal)}
        >
          Reset Password
        </ModalHeader>
        <ModalBody>
          {successMessage && <Alert color="success">{successMessage}</Alert>}
          {errorMessage && <Alert color="danger">{errorMessage}</Alert>}
          <h5 className="mb-4">
            Please check your email <strong>{userDetailsData?.email}</strong> for OTP.
          </h5>
          <Form onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}>
            <div className="mb-3">
              <Label for="newPassword">New Password</Label>
              <Input
                type="password"
                name="password"
                id="newPassword"
                placeholder="Enter your new password"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.password}
                invalid={
                  validation.touched.password && validation.errors.password
                    ? true
                    : false
                }
              />
              {validation.touched.password && validation.errors.password ? (
                <FormFeedback type="invalid">
                  {validation.errors.password}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label for="newPassword">Confirm Password</Label>
              <Input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                onBlur={validation.handleBlur}
                placeholder="Enter your new password"
                onChange={validation.handleChange}
                value={validation.values.confirmPassword}
                invalid={
                  validation.touched.confirmPassword && validation.errors.confirmPassword
                    ? true
                    : false
                }
              />
              {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                <FormFeedback type="invalid">
                  {validation.errors.confirmPassword}
                </FormFeedback>
              ) : null}
            </div>
            <div className="mb-3">
              <Label for="otp">OTP Code</Label>
              <Input
                type="text"
                name="otp"
                id="otp"
                placeholder="Enter the OTP code"
                onBlur={validation.handleBlur}
                onChange={validation.handleChange}
                value={validation.values.otp}
                invalid={
                  validation.touched.otp && validation.errors.otp
                    ? true
                    : false
                }
              />
              {validation.touched.otp && validation.errors.otp ? (
                <FormFeedback type="invalid">
                  {validation.errors.otp}
                </FormFeedback>
              ) : null}
            </div>
            <Button color="primary" type="submit" className="w-100">
              Submit
            </Button>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default withRouter(UserProfile);
